<template>
  <div class="create-deal-modal">
    <div class="create-deal-modal__body">
      <!-- Title -->
      <TextInput
        id="deal-title"
        v-model="form.title"
        :placeholder="$t('deals.title_placeholder')"
        :error="errors.title"
        :label="$t('deals.title')"
      />

      <!-- Value -->
      <div class="create-deal-modal__form-row">
        <div class="create-deal-modal__form-group">
          <TextInput
            id="deal-value"
            v-model="form.value"
            type="number"
            :placeholder="$t('deals.value_placeholder')"
            :label="$t('deals.value')"
          >
            <template #prefix>€</template>
          </TextInput>
        </div>

        <div class="create-deal-modal__form-group">
          <TextInput
            id="deal-close-date"
            v-model="form.expected_close_date"
            type="date"
            :label="$t('deals.expected_close_date')"
          />
        </div>
      </div>

      <!-- Pipeline & Stage -->
      <div class="create-deal-modal__form-row">
        <div class="create-deal-modal__form-group">
          <SelectInput
            id="deal-pipeline"
            v-model="form.pipeline_id"
            :options="pipelineOptions"
            :placeholder="$t('deals.select_pipeline')"
            @update:model-value="handlePipelineChange"
            :label="$t('deals.pipeline')"
          />
        </div>

        <div class="create-deal-modal__form-group">
          <SelectInput
            id="deal-stage"
            v-model="form.stage_id"
            :options="stageOptions"
            :placeholder="$t('deals.select_stage')"
            :label="$t('deals.stage')"
          />
        </div>
      </div>

      <!-- Lead (Required) -->
      <div class="create-deal-modal__form-group">
        <SearchableLeadSelect
          id="deal-lead"
          v-model="form.lead_id"
          :placeholder="$t('deals.select_lead')"
          :error="errors.lead_id"
          @leadSelected="onLeadSelected"
        >
          <template #label>
            <label for="deal-lead">
              {{ $t('deals.lead') }} *
              <a
                href="#"
                @click.prevent="openCreateLeadModal"
                class="create-deal-modal__create-link"
              >
                {{ $t('crm.create_lead.title') }}
              </a>
            </label>
          </template>
        </SearchableLeadSelect>
        <p class="create-deal-modal__help-text">
          {{ $t('deals.lead_help') }}
        </p>
      </div>

      <!-- Owner -->
      <div class="create-deal-modal__form-group">
        <label>{{ $t('deals.owner') }}</label>
        <SelectInput
          id="deal-owner"
          v-model="form.owner_id"
          :options="ownerOptions"
          :placeholder="$t('deals.select_owner')"
        />
      </div>

      <!-- Internal Notes -->
      <div class="create-deal-modal__form-group">
        <label>{{ $t('deals.internal_notes') }}</label>
        <textarea
          v-model="form.internal_notes"
          :placeholder="$t('deals.internal_notes_placeholder')"
          rows="3"
        ></textarea>
      </div>
    </div>

    <div class="form-actions">
      <Btn variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn variant="primary" @click="handleSubmit" :disabled="!isValid || loading">
        {{ loading ? $t('common.creating') : $t('deals.create_deal') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
  import { useDealStore } from '@/store/dealStore';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { crmService } from '@/services/crmService';
  import OrganizationService from '@/services/organizationService';

  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import SearchableLeadSelect from '@/components/inputs/SearchableLeadSelect.vue';
  import CreateEditLead from '@/components/crm/CreateEditLead.vue';
  import Btn from '@/components/partials/Btn.vue';

  const organizationService = new OrganizationService();

  export default {
    name: 'CreateDealModal',

    components: {
      TextInput,
      SelectInput,
      SearchableLeadSelect,
      Btn,
    },

    props: {
      pipelineId: {
        type: String,
        default: null,
      },
      stageId: {
        type: String,
        default: null,
      },
      preselectedLeadId: {
        type: String,
        default: null,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
      onCancel: {
        type: Function,
        default: () => {},
      },
      onError: {
        type: Function,
        default: () => {},
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const dealStore = useDealStore();
      const userStore = useUserStore();
      const uiStore = useUIStore();

      const loading = ref(false);
      const currentLead = ref(null);
      const members = ref([]);
      const errors = ref({});

      const form = ref({
        title: '',
        value: 0,
        currency: 'EUR',
        pipeline_id: props.pipelineId || null,
        stage_id: null,
        lead_id: props.preselectedLeadId || null,
        owner_id: null,
        expected_close_date: '',
        internal_notes: '',
      });

      const pipelines = computed(() => dealStore.pipelines);

      const pipelineOptions = computed(() => {
        return pipelines.value.map((pipeline) => ({
          value: pipeline.id,
          label: pipeline.name,
        }));
      });

      const selectedPipeline = computed(() => {
        return pipelines.value.find((p) => p.id === form.value.pipeline_id);
      });

      const stageOptions = computed(() => {
        if (!selectedPipeline.value?.stages) return [];
        return selectedPipeline.value.stages.map((stage) => ({
          value: stage.id,
          label: stage.name,
        }));
      });

      const ownerOptions = computed(() => {
        if (!Array.isArray(members.value)) return [];
        return members.value.map((member) => {
          const user = member.user || member;
          const id = user?.id ?? member.user_id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.email ||
            member.email ||
            id;
          return { value: id, label };
        });
      });

      const onLeadSelected = (lead) => {
        currentLead.value = lead;
      };

      const openCreateLeadModal = () => {
        const modalId = uiStore.addModal(CreateEditLead, {
          title: $t('crm.create_lead.title'),
          description: $t('crm.create_lead.description'),
          size: 'lg',
          hasComponent: true,
          onCreated: (newLead) => {
            form.value.lead_id = newLead.id;
            currentLead.value = newLead;
            onLeadSelected(newLead);
            uiStore.closeModal(modalId);
          },
          onClose: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const isValid = computed(() => {
        return (
          form.value.title?.trim() &&
          form.value.pipeline_id &&
          form.value.stage_id &&
          form.value.lead_id
        );
      });

      const handlePipelineChange = (pipelineId) => {
        const pipeline = pipelines.value.find((p) => p.id === pipelineId);
        if (pipeline?.stages?.length > 0) {
          const preselected = pipeline.stages.find((s) => s.id === props.stageId);
          form.value.stage_id = (preselected || pipeline.stages[0]).id;
        } else {
          form.value.stage_id = null;
        }
      };

      const fetchMembers = async () => {
        try {
          const organizationId = userStore.currentOrganization?.id;
          if (!organizationId) {
            console.warn('No organization ID available');
            members.value = [];
            return;
          }
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          console.error('Failed to fetch members:', error);
          members.value = [];
        }
      };

      const validate = () => {
        errors.value = {};

        if (!form.value.title?.trim()) {
          errors.value.title = $t('deals.title_required');
        }

        if (!form.value.lead_id) {
          errors.value.lead_id = $t('deals.lead_required');
        }

        if (!form.value.pipeline_id) {
          errors.value.pipeline_id = $t('deals.pipeline_required');
        }

        if (!form.value.stage_id) {
          errors.value.stage_id = $t('deals.stage_required');
        }

        return Object.keys(errors.value).length === 0;
      };

      const handleSubmit = async () => {
        if (!validate()) {
          props.onError(errors.value);
          return;
        }

        loading.value = true;

        try {
          const dealData = {
            title: form.value.title.trim(),
            value: Number(form.value.value) || 0,
            currency: form.value.currency,
            pipeline_id: form.value.pipeline_id,
            stage_id: form.value.stage_id,
            lead_id: form.value.lead_id,
            owner_id: form.value.owner_id || null,
            expected_close_date: form.value.expected_close_date || null,
            internal_notes: form.value.internal_notes || null,
          };

          const deal = await dealStore.createDeal(dealData);
          props.onSubmit(deal);
        } catch (error) {
          console.error('Failed to create deal:', error);
          props.onError({ submit: error.message });
        } finally {
          loading.value = false;
        }
      };

      const handleCancel = () => {
        props.onCancel();
      };

      // Initialize
      onMounted(async () => {
        // Fetch pipelines if not loaded
        if (pipelines.value.length === 0) {
          await dealStore.fetchPipelines();
        }

        // Set default pipeline
        if (!form.value.pipeline_id && pipelines.value.length > 0) {
          const defaultPipeline = pipelines.value.find((p) => p.is_default) || pipelines.value[0];
          form.value.pipeline_id = defaultPipeline.id;
          handlePipelineChange(defaultPipeline.id);
        } else if (form.value.pipeline_id) {
          handlePipelineChange(form.value.pipeline_id);
        }

        // Set current user as default owner
        if (userStore.user?.id) {
          form.value.owner_id = userStore.user.id;
        }

        // Fetch members
        await fetchMembers();
      });

      // Watch for external pipeline changes
      watch(
        () => props.pipelineId,
        (newId) => {
          if (newId && newId !== form.value.pipeline_id) {
            form.value.pipeline_id = newId;
            handlePipelineChange(newId);
          }
        }
      );

      return {
        form,
        errors,
        loading,
        pipelineOptions,
        stageOptions,
        ownerOptions,
        currentLead,
        isValid,
        handlePipelineChange,
        handleSubmit,
        handleCancel,
        onLeadSelected,
        openCreateLeadModal,
      };
    },
  };
</script>

<style scoped lang="scss">
  .create-deal-modal {
    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      margin-top: 1.5rem;
    }

    &__form-group {
      margin-bottom: 1rem;

      label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-700);
        margin-bottom: 0.5rem;
      }

      textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid var(--slate-200);
        border-radius: 0.5rem;
        font-size: 0.875rem;
        resize: vertical;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: var(--brand-400);
          box-shadow: 0 0 0 3px var(--brand-100);
        }
      }
    }

    &__form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;

      @media (max-width: 640px) {
        grid-template-columns: 1fr;
      }
    }

    &__help-text {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0.5rem 0 0 0;
    }

    &__create-link {
      font-size: 0.875rem;
      color: var(--brand-500);
      text-decoration: none;
      font-weight: 500;
      margin-left: 0.5rem;
      transition: color 0.2s ease;

      &:hover {
        color: var(--brand-600);
        text-decoration: underline;
      }
    }
  }
</style>
